// @ts-strict-ignore
import moment from 'moment-timezone';
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  Box,
  IconName,
  Text,
  Token,
  useSnackBar,
  ValuePair,
} from '@usealloy/component-library';

import TagSquare from '~/components/TagSquare';
import { UserContext } from '~/dashboard/context';
import type { Document } from '~/entities/types';
import apiCalls from '~/helpers/apiCalls';
import { routes } from '~/helpers/routes';
import type { EntitiesMap } from '../DocumentReviewModal';
import { getDocReviewBadgeProps } from '../helpers/getReviewBadgeStatus';

// ============================================================
//     TYPES
// ============================================================
interface Props {
  document: Document;
  entities?: EntitiesMap;
  onRenameDocSuccess?: () => void;
  tags?: {
    name: string;
    color: string;
  }[];
  servicesRun?: string[];
}

// ============================================================
//     STYLED COMPONENTS
// ============================================================
const StyledHeaderContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min-content, 120px))',
  gridAutoRows: 'min-content 1fr',
  rowGap: 'var(--cl-space-2)',
  columnGap: 'var(--cl-space-4)',
  width: '100%',
  paddingBlock: 'var(--cl-space-2)',
  borderBottom: '1px solid var(--cl-border-default)',

  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const StyledItem = styled.div({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridTemplateRows: 'subgrid',
  gridRow: 'span 2',
  alignItems: 'center',
});

const StyledValuePair = styled(ValuePair)({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridTemplateRows: 'subgrid',
  gridRow: 'span 2',
  alignItems: 'center',
  gap: 'inherit',

  '& > *': {
    minWidth: 'max-content',
  },

  '& label': {
    alignItems: 'flex-end',
    lineHeight: 'var(--cl-font-size-1)',
  },

  '& p': {
    paddingRight: 'var(--cl-space-2)',
  },
});

const HeaderItem: React.FC<{
  children: React.ReactNode;
  title: string;
}> = ({ children, title }) => (
  <StyledItem>
    <Text
      as="span"
      size={1}
      style={{
        alignSelf: 'flex-end',
        color: 'var(--cl-text-muted)',
      }}
    >
      {title}
    </Text>
    {children}
  </StyledItem>
);

// ============================================================
//     COMPONENT
// ============================================================
export const DocReviewHeader: React.FC<Props> = ({
  document,
  entities,
  onRenameDocSuccess,
  servicesRun,
  tags,
}) => {
  const addSnackbar = useSnackBar();
  const { typhonDomain } = useContext(UserContext);

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedUploadDate = moment.tz(document.timestamp, 'UTC').tz(userTimezone).format('LLL z');

  const [docUpdatedName, setDocUpdatedName] = useState<string>(document.name);

  const hasTags = tags?.length > 0;
  const hasServicesRun = servicesRun?.length > 0;

  // Entity display for multi-entity/group investigations only
  const showEntityField = !!entities && Object.keys(entities).length > 1;
  const entity = entities?.[document.entity_token];
  const entityDisplayName = entity?.entity_name || document.entity_token;
  const entityProfileUrl = routes.entities.detail(document.entity_token);

  const { iconName, label, status } = getDocReviewBadgeProps(document.approval_outcome);

  const {
    mutateAsync: updateDocName,
  } = useMutation({
    mutationFn: async (updatedName: string) => {
      const trimmedName = updatedName.trim();
      if (!trimmedName) {
        throw new Error('Document name cannot be empty');
      }
      if (trimmedName === document.name) { return null; }

      return apiCalls.entities.documents.renameDocument({
        documentToken: document.document_token,
        entityToken: document.entity_token,
        name: trimmedName,
        typhonDomain,
      });
    },
    onSuccess: (_, updatedName) => {
      addSnackbar({ title: `Document name changed to ${updatedName}`, type: 'success' });
      onRenameDocSuccess?.();
    },
    onError: () => {
      addSnackbar({ title: 'Document Rename Failed', type: 'danger' });
    },
  });

  return (
    <StyledHeaderContainer
      data-pendo-anchor="document-review-metadata"
    >
      <HeaderItem title="Status">
        <Badge status={status} icon={iconName} iconSize="small">{label}</Badge>
      </HeaderItem>
      <StyledValuePair
        dataType="string"
        label="Name"
        value={docUpdatedName}
        onChange={value => { setDocUpdatedName(value); }}
        onSave={value => { updateDocName(value.toString()); }}
        data-pendo-anchor="document-review-document-name"
      />
      <HeaderItem title="Type">
        <Text>
          {document.document_type}
        </Text>
      </HeaderItem>
      <HeaderItem title="Uploaded At">
        <Text style={{ minWidth: 'max-content' }}>
          {formattedUploadDate}
        </Text>
      </HeaderItem>
      {showEntityField && (
        <HeaderItem title="Entity">
          <Text style={{ whiteSpace: 'nowrap' }}>
            <Link
              className="secondary inline"
              to={entityProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {entityDisplayName}
            </Link>
          </Text>
        </HeaderItem>
      )}
      {hasTags && (
        <HeaderItem title="Tags">
          <Box justifyContent="flex-start" wrap="wrap" gap={1}>
            {tags?.map(tag => (
              <TagSquare key={tag.name} id={tag.name} color={tag.color} tooltipText={tag.name} />
            ))}
          </Box>
        </HeaderItem>
      )}
      {hasServicesRun && (
        <HeaderItem title="Service">
          <Box justifyContent="flex-start" wrap="wrap" gap={1}>
            {servicesRun.map(serviceName => (
              <Badge key={serviceName} icon={IconName.Service} iconSize="small" status="eventSuccess">{serviceName}</Badge>
            ))}
          </Box>
        </HeaderItem>
      )}
      <HeaderItem title="Token">
        <Token displayContent={document.document_token} />
      </HeaderItem>
    </StyledHeaderContainer>
  );
};
